import { useState } from "react";
import { Check, Trash2, X } from "lucide-react";
import { dayjs } from "@boo/shared";
import type { ClassifyIncomingRequest, Transaction } from "@boo/shared";
import { formatSource } from "../../lib/formatSource";
import IncomingClassifier from "./IncomingClassifier";

interface IncomingReviewRowProps {
  transaction: Transaction;
  onClassify: (id: string, payload: ClassifyIncomingRequest) => Promise<void>;
  onDelete: (id: string) => void;
}

// Money received that is not counted anywhere until the user says what it is.
export default function IncomingReviewRow({
  transaction,
  onClassify,
  onDelete,
}: IncomingReviewRowProps) {
  const [confirming, setConfirming] = useState(false);

  return (
    <div className="flex flex-col gap-2.5 px-3 sm:px-4 py-3.5 rounded-[10px] bg-card border-[0.5px] border-[var(--color-primary-border)]">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5 min-w-0">
          <div className="text-[13px] font-medium truncate text-foreground">
            {transaction.displayTitle ?? transaction.title}
          </div>
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <span>{formatSource(transaction.source)}</span>
            <span className="w-0.5 h-0.5 rounded-full bg-neutral-400 inline-block" />
            <span>{dayjs(transaction.date).format("D MMM")}</span>
            <span className="w-0.5 h-0.5 rounded-full bg-neutral-400 inline-block" />
            <span>money in · not counted yet</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <div className="text-[14px] font-bold [font-variant-numeric:tabular-nums] text-muted-foreground">
            + ฿{transaction.amount.toLocaleString("en-US")}
          </div>
          {confirming ? (
            <>
              <button
                onClick={() => setConfirming(false)}
                className="p-2 sm:p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-[var(--surface-muted)] transition-colors cursor-pointer"
                aria-label="Cancel delete"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => onDelete(transaction.id)}
                className="p-2 sm:p-1 rounded-md text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors cursor-pointer"
                aria-label="Confirm delete"
              >
                <Check className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <button
              onClick={() => setConfirming(true)}
              className="p-2 sm:p-1 rounded-md text-muted-foreground opacity-40 hover:opacity-100 hover:text-red-500 hover:bg-[var(--surface-muted)] transition-all cursor-pointer"
              aria-label="Delete transaction"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      <IncomingClassifier transaction={transaction} onClassify={onClassify} />
    </div>
  );
}
